import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { registerUser, useAuth, useAppDispatch } from "../redux";
import MainLogo from "../components/common/MainLogo";
import Spinner from "../components/common/Spinner";
import PhoneNumberInput from "../components/common/PhoneNumberInput";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import ReCAPTCHA from "react-google-recaptcha";
import { validateForm, getPasswordStrength } from "../utils/validation";
import { showToast } from "../utils/toast";
import { setUser } from "../redux/slices/authSlice";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const API_BASE_URL = (() => {
  const raw = process.env.REACT_APP_API_BASE_URL || "https://api.ibrlive.com";
  return raw.replace(/\/+$/, "").replace(/\/api$/i, "");
})();

const Register = () => {
  const [formData, setFormData] = useState({
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
    recaptcha: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAuth();

  const handleGoogleResponse = useCallback(
    async (response) => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/google`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken: response.credential }),
        });

        const data = await res.json();
        if (!res.ok || data.error) {
          console.error("Google sign-up failed:", data.error || res.statusText);
          showToast.error(
            data.error || "Google sign-up failed. Please try again."
          );
          return;
        }

        if (data.token) {
          localStorage.setItem("token", data.token);
        }
        if (data.refreshToken) {
          localStorage.setItem("refreshToken", data.refreshToken);
        }
        if (data.user) {
          localStorage.setItem("userData", JSON.stringify(data.user));
          dispatch(setUser(data.user));
        }

        const selectedPlanRaw = localStorage.getItem("selectedPlan");
        if (selectedPlanRaw) {
          localStorage.removeItem("selectedPlan");
          const planData = JSON.parse(selectedPlanRaw);
          navigate("/dashboard/subscription", {
            replace: true,
            state: {
              selectedPlan: planData.planName,
              billingCycle: planData.billingCycle,
              currency: planData.currency,
              autoSelect: true,
              customApiRequests: planData.customApiRequests || null,
            },
          });

          showToast.success(
            "Account created! Redirecting to your selected plan..."
          );
        } else {
          const isAdmin = data.user?.role === "admin";
          navigate(isAdmin ? "/admin-dashboard" : "/dashboard", {
            replace: true,
          });
          showToast.success("Account created successfully with Google!");
        }
      } catch (err) {
        console.error("Google sign-up failed:", err);
        showToast.error("Google sign-up failed. Please try again.");
      }
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    if (!window.google || !GOOGLE_CLIENT_ID) return;
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleResponse,
    });

    const btn = document.getElementById("googleSignInDivRegister");
    if (btn) {
      window.google.accounts.id.renderButton(btn, {
        theme: "outline",
        size: "large",
      });
    }
  }, [handleGoogleResponse]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name] && name !== "confirmPassword") {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (name === "confirmPassword") {
      if (formData.password && value !== formData.password) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "Passwords do not match",
        }));
      } else if (formData.password && value === formData.password) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "",
        }));
      }
    } else if (name === "password") {
      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "Passwords do not match",
        }));
      } else if (formData.confirmPassword && value === formData.confirmPassword) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "",
        }));
      }
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  const handleMobileBlur = () => {
    setTouched((prev) => ({
      ...prev,
      mobile: true,
    }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    const validation = validateForm(formData, "register");
    if (!validation.isValid) {
      setErrors(validation.errors);
      setTouched({
        email: true,
        mobile: true,
        password: true,
        confirmPassword: true,
        recaptcha: true,
      });
      return;
    }

    try {
      await dispatch(
        registerUser({
          email: formData.email.trim(),
          mobile: formData.mobile.trim(),
          password: formData.password,
          recaptcha: formData.recaptcha,
        })
      );
      showToast.success(
        "Registration successful! Please check your email to verify your account."
      );
      navigate("/check-email");
    } catch (error) {
      console.error("Registration failed:", error);
    }
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <>
      <Header />
      <div
        className="container-fluid auth-container"
        style={{
          minHeight: "calc(100vh - 200px)",
          paddingTop: "10rem",
          paddingBottom: "8rem",
        }}
      >
        <div
          className="container d-flex justify-content-center align-items-center"
          style={{ minHeight: "50vh" }}
        >
          <div
            className="card p-4 shadow-lg"
            style={{ width: "100%", maxWidth: "450px" }}
          >
            <div className="d-flex justify-content-center mb-4">
              <MainLogo />
            </div>
            <h3 className="text-center mb-2">Create a New Account</h3>
            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleRegister}>
              {/* Email */}
              <div className="mb-3">
                <label htmlFor="email" className="form-label">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`form-control ${
                    touched.email && errors.email ? "is-invalid" : ""
                  }`}
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading}
                />
                {touched.email && errors.email && (
                  <div className="invalid-feedback">{errors.email}</div>
                )}
              </div>

              {/* Phone Number */}
              <div className="mb-3">
                <label htmlFor="mobile" className="form-label">
                  Phone Number *
                </label>
                <PhoneNumberInput
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  onBlur={handleMobileBlur}
                  disabled={loading}
                  hasError={touched.mobile && !!errors.mobile}
                  placeholder="Enter phone number"
                />
                {touched.mobile && errors.mobile && (
                  <div className="text-danger mt-1">
                    <small>{errors.mobile}</small>
                  </div>
                )}
              </div>

              {/* Password */}
              <div className="mb-3">
                <label htmlFor="password" className="form-label">
                  Password *
                </label>
                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    className={`form-control ${
                      touched.password && errors.password ? "is-invalid" : ""
                    }`}
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                {touched.password && errors.password && (
                  <div className="invalid-feedback">{errors.password}</div>
                )}

                {formData.password && (
                  <div className="mt-2">
                    <div className="d-flex align-items-center mb-1">
                      <small className="text-muted me-2">
                        Password strength:
                      </small>
                      <div
                        className="progress flex-grow-1"
                        style={{ height: "4px" }}
                      >
                        <div
                          className={`progress-bar ${
                            passwordStrength.strength <= 2
                              ? "bg-danger"
                              : passwordStrength.strength <= 3
                              ? "bg-warning"
                              : "bg-success"
                          }`}
                          style={{
                            width: `${(passwordStrength.strength / 4) * 100}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                    {passwordStrength.feedback.length > 0 && (
                      <small className="text-muted">
                        Missing: {passwordStrength.feedback.join(", ")}
                      </small>
                    )}
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="mb-3">
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm Password *
                </label>
                <div className="input-group">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    className={`form-control ${
                      (touched.confirmPassword || formData.confirmPassword) &&
                      errors.confirmPassword
                        ? "is-invalid"
                        : ""
                    }`}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                  >
                    {showConfirmPassword ? "Hide" : "Show"}
                  </button>
                </div>
                {(touched.confirmPassword || formData.confirmPassword) &&
                  errors.confirmPassword && (
                    <div className="text-danger mt-1">
                      <small>{errors.confirmPassword}</small>
                    </div>
                  )}
              </div>

              {/* reCAPTCHA */}
              <div className="mb-3 d-flex justify-content-center">
                <ReCAPTCHA
                  sitekey="6LcME8krAAAAAJ6WuRag6iHZQXn0o2GK8Iw7DtSE"
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, recaptcha: value }))
                  }
                  onExpired={() =>
                    setFormData((prev) => ({ ...prev, recaptcha: "" }))
                  }
                  onError={(error) => {
                    console.error("reCAPTCHA error:", error);
                    showToast.error(
                      "reCAPTCHA failed to load. Please refresh the page."
                    );
                  }}
                  theme="light"
                  size="normal"
                />
                {touched.recaptcha && errors.recaptcha && (
                  <div className="text-danger mt-1 text-center w-100">
                    <small>{errors.recaptcha}</small>
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={loading || !formData.recaptcha}
              >
                {loading ? <Spinner /> : "Register"}
              </button>

              <div className="mt-3">
                <span className="text-muted small d-block mb-2 text-center">
                  Or continue with
                </span>
                <div
                  id="googleSignInDivRegister"
                  style={{ display: "flex", justifyContent: "center" }}
                />
              </div>
            </form>

            <div className="text-center mt-3">
              <small className="text-muted">
                Already have an account? <Link to="/login">Login</Link>
              </small>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Register;
